// libs
import React, { memo, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useParams } from 'react-router-dom';

// components
import Image from 'shared/components/Image';
import IOElement from 'shared/components/IOElement';
import Placeholder from './Placeholder';

// contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// styles
import { Wrapper, ImageItem } from './styles';

// helpers
import { getOptimisedImageUrl } from 'shared/helpers/utils';

// store
import { fetchAllImagesFromCustomerReview as _fetchAllImagesFromCustomerReview } from 'shared/store/details/services';

// constants
import { BASE_GUTTER_SPACE } from 'shared/styles/base';
import { TAB_INDEX, ROLES } from 'shared/constants';

const IMAGE_ALT = 'Review pic uploaded by user';
const NUMBER_OF_COLUMNS = 3;
const DEFAULT_ASPECT_RATIO = 0.75;

export interface IImageGridProps {
  imageList: ReviewAllImages.IImageItem[];
  handleClick?: (image: ReviewAllImages.IImageClickCallBack) => void;
  isFetching: boolean;
  isError: boolean;
  totalCount: number;
  fetchAllImagesFromCustomerReview: typeof _fetchAllImagesFromCustomerReview;
  pageKey: string;
}

function ImageGrid({
  imageList = [],
  handleClick,
  isFetching,
  isError,
  totalCount,
  fetchAllImagesFromCustomerReview,
  pageKey,
}: IImageGridProps) {
  const { id: productId } = useParams<{ id: string; }>();
  const { deviceWidth } = useResponsiveContext();
  const itemWidth = useMemo(
    () => deviceWidth / NUMBER_OF_COLUMNS - BASE_GUTTER_SPACE,
    [deviceWidth]
  );

  const loadMore = () => {
    if (isFetching) {
      return;
    }

    if (totalCount <= imageList.length) {
      return;
    }

    fetchAllImagesFromCustomerReview({ productId, pageKey });
  };

  if (isFetching) {
    return <Placeholder />;
  }

  if (isError || isEmpty(imageList)) {
    return null;
  }

  return (
    <Wrapper>
      {imageList.map((image, index) => {
        const onClick = () => {
          handleClick && handleClick({ ...image, index });
        };

        return (
          <ImageItem key={'image_grid_' + index} onClick={onClick} role={ROLES.BUTTON} tabIndex={TAB_INDEX.ACTION_INVISIBILITY}>
            <Image
              imgClassName="reviewImage"
              src={getOptimisedImageUrl(image.url, { width: itemWidth * 2 })}
              alt={IMAGE_ALT}
              aspectRatio={DEFAULT_ASPECT_RATIO}
              width={itemWidth}
            />
          </ImageItem>
        );
      })}
      <IOElement onVisible={loadMore} />
    </Wrapper>
  );
}

export default memo(ImageGrid);
